import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/shared/components/LoadingSpinner";
import { MERCHANT_BILLING_ENABLED } from "@/shared/config/merchantBilling";
import {
  DEFAULT_DASHBOARD_ANALYTICS_SLUG,
  dashboardAnalyticsHref,
} from "@/shared/navigation/analyticsNav";
import { AddressValidationSectionOutlet } from "./AddressValidationSectionOutlet";
import { AdminSectionOutlet } from "./AdminSectionOutlet";
import { GlobalRouteError } from "./GlobalRouteError";
import { HomeEntryRedirect } from "./HomeEntryRedirect";
import { ProblemSolveSectionOutlet } from "./ProblemSolveSectionOutlet";
import { ProtectedRoute } from "./ProtectedRoute";

// Lazy load pages for code splitting
const LoginPage = lazy(() => import("@/pages/auth/Login"));
const RegisterPage = lazy(() => import("@/pages/auth/Register"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPassword"));
const DashboardPage = lazy(() =>
  import("@/pages/dashboard/Dashboard").then((m) => ({ default: m.Dashboard }))
);
const OrdersPage = lazy(() => import("@/pages/orders/Orders"));
const OrderDetailRouter = lazy(() => import("@/pages/orders/OrderDetailRouter"));
const ShipmentV4DetailPage = lazy(() => import("@/pages/orders/ShipmentV4Detail"));
const CreateOrderV4Page = lazy(() => import("@/pages/orders/CreateOrderV4"));
const EditOrderPage = lazy(() => import("@/pages/orders/EditOrderRouter"));
const StoresPage = lazy(() => import("@/pages/stores/Stores"));
const StoreDetailPage = lazy(() => import("@/pages/stores/StoreDetail"));
const CreateStorePage = lazy(() => import("@/pages/stores/CreateStore"));
const GenerateStoreAccessTokenPage = lazy(() => import("@/pages/stores/GenerateStoreAccessToken"));
const EditStorePage = lazy(() => import("@/pages/stores/EditStore"));
const UsersPage = lazy(() => import("@/pages/users/Users"));
const UserDetailPage = lazy(() => import("@/pages/users/UserDetail"));
const CreateUserPage = lazy(() => import("@/pages/users/CreateUser"));
const EditUserPage = lazy(() => import("@/pages/users/EditUser"));
const ManifestsPage = lazy(() => import("@/pages/manifests/Manifests"));
const CollectedManifestsPage = lazy(() => import("@/pages/manifests/CollectedManifests"));
const ManifestDetailPage = lazy(() => import("@/pages/manifests/ManifestDetail"));
const AddressValidationQueuePage = lazy(
  () => import("@/pages/addressValidation/AddressValidationQueue")
);
const AddressValidationComparePage = lazy(
  () => import("@/pages/addressValidation/AddressValidationCompare")
);
const ProblemSolveQueuePage = lazy(() => import("@/pages/problem-solve/ProblemSolveQueue"));
const ProblemSolveShipmentPage = lazy(() => import("@/pages/problem-solve/ProblemSolveShipment"));
const LocationsPage = lazy(() => import("@/pages/locations/Locations"));
const LocationDetailPage = lazy(() => import("@/pages/locations/LocationDetail"));
const CreateLocationPage = lazy(() => import("@/pages/locations/CreateLocation"));
const EditLocationPage = lazy(() => import("@/pages/locations/EditLocation"));
const PackageTypesListPage = lazy(() => import("@/pages/packageTypes/PackageTypesList"));
const CreatePackageTypePage = lazy(() => import("@/pages/packageTypes/CreatePackageType"));
const EditPackageTypePage = lazy(() => import("@/pages/packageTypes/EditPackageType"));
const CustomersPage = lazy(() => import("@/pages/customers/Customers"));
const CustomerDetailPage = lazy(() => import("@/pages/customers/CustomerDetail"));
const CreateCustomerPage = lazy(() => import("@/pages/customers/CreateCustomer"));
const EditCustomerPage = lazy(() => import("@/pages/customers/EditCustomer"));
const SettingsPage = lazy(() => import("@/pages/settings/Settings"));
const JourneyAnalyticsPage = lazy(() =>
  import("@/features/analytics-journeys/JourneyAnalytics").then((m) => ({
    default: m.JourneyAnalytics,
  }))
);
const SysOpsCatchAllPage = lazy(() => import("@/pages/sysops/SysOpsCatchAll"));
const AdminCreateCustomStorePage = lazy(() => import("@/pages/admin/AdminCreateCustomStore"));
const AdminGetOrderKeyPage = lazy(() => import("@/pages/admin/AdminGetOrderKey"));
const AdminMintsoftCredentialsPage = lazy(() => import("@/pages/admin/AdminMintsoftCredentials"));
const ReceivingHubsPage = lazy(() => import("@/pages/receiving/ReceivingHubs"));
const ReceivingAtHubPage = lazy(() => import("@/pages/receiving/ReceivingAtHub"));
const ReceivingCreateHubPage = lazy(() => import("@/pages/receiving/ReceivingCreateHub"));
const ReceivingUpdateHubPage = lazy(() => import("@/pages/receiving/ReceivingUpdateHub"));
const SysOpsHomePage = lazy(() => import("@/pages/sysops/SysOpsHome"));
const SysOpsHubsListPage = lazy(() => import("@/pages/sysops/SysOpsHubsList"));
const SysOpsHubDetailPage = lazy(() => import("@/pages/sysops/SysOpsHubDetail"));
const SysOpsCreateHubPage = lazy(() => import("@/pages/sysops/SysOpsCreateHub"));
const TrackOrderPage = lazy(() => import("@/pages/public/TrackOrder"));
const TrackOrderDetailPage = lazy(() => import("@/pages/public/TrackOrderDetail"));
const TrackOrderReturnPage = lazy(() => import("@/pages/public/TrackOrderReturn"));
const ShopifyCallbackPage = lazy(() => import("@/pages/public/ShopifyCallback"));
const ShopifySuccessPage = lazy(() => import("@/pages/public/ShopifySuccess"));
const WooCommerceCallbackPage = lazy(() => import("@/pages/public/WooCommerceCallback"));
const WooCommerceSuccessPage = lazy(() => import("@/pages/public/WooCommerceSuccess"));
const SysOpsMerchantsListPage = lazy(() => import("@/pages/sysops/SysOpsMerchantsList"));
const SysOpsMerchantDetailPage = lazy(() => import("@/pages/sysops/SysOpsMerchantDetail"));
const SysOpsMerchantBillingPage = lazy(() => import("@/pages/sysops/SysOpsMerchantBilling"));
const SysOpsCreateMerchantPage = lazy(() => import("@/pages/sysops/SysOpsCreateMerchant"));
const SysOpsMerchantOnboardingPage = lazy(() => import("@/pages/sysops/SysOpsMerchantOnboarding"));
const SysOpsMerchantOnboardingDetailPage = lazy(
  () => import("@/pages/sysops/SysOpsMerchantOnboardingDetail")
);
const SysOpsDriverOnboardingPage = lazy(() => import("@/pages/sysops/SysOpsDriverOnboarding"));
const SysOpsDriverAppealsPage = lazy(() => import("@/pages/sysops/SysOpsDriverAppeals"));
const SysOpsBillingExceptionsPage = lazy(() => import("@/pages/sysops/SysOpsBillingExceptions"));
const SysOpsPromiseExceptionsPage = lazy(() => import("@/pages/sysops/SysOpsPromiseExceptions"));
const SysOpsDriverOnboardingDetailPage = lazy(
  () => import("@/pages/sysops/SysOpsDriverOnboardingDetail")
);
const SysOpsRatesListPage = lazy(() => import("@/pages/sysops/SysOpsRatesList"));
const SysOpsCreateRatePage = lazy(() => import("@/pages/sysops/SysOpsCreateRate"));
const SysOpsRateDetailPage = lazy(() => import("@/pages/sysops/SysOpsRateDetail"));
const SysOpsServiceTypesListPage = lazy(() => import("@/pages/sysops/SysOpsServiceTypesList"));
const SysOpsCreateServiceTypePage = lazy(() => import("@/pages/sysops/SysOpsCreateServiceType"));
const SysOpsServiceTypeDetailPage = lazy(() => import("@/pages/sysops/SysOpsServiceTypeDetail"));
const SysOpsGeofencesListPage = lazy(() => import("@/pages/sysops/SysOpsGeofencesList"));
const SysOpsGeofenceDetailPage = lazy(() => import("@/pages/sysops/SysOpsGeofenceDetail"));
const SysOpsCreateGeofencePage = lazy(() => import("@/pages/sysops/SysOpsCreateGeofence"));
const SysOpsUpdateGeofencePage = lazy(() => import("@/pages/sysops/SysOpsUpdateGeofence"));
const SysOpsDriverZoneGeofencesPage = lazy(
  () => import("@/pages/sysops/driverZones/SysOpsDriverZoneGeofences")
);
const SysOpsZonePlansListPage = lazy(
  () => import("@/pages/sysops/driverZones/SysOpsZonePlansList")
);
const SysOpsZonePlanDetailPage = lazy(
  () => import("@/pages/sysops/driverZones/SysOpsZonePlanDetail")
);
const SysOpsEventsListPage = lazy(() => import("@/pages/sysops/SysOpsEventsList"));
const SysOpsEventDetailPage = lazy(() => import("@/pages/sysops/SysOpsEventDetail"));
const SysOpsDriversListPage = lazy(() => import("@/pages/sysops/SysOpsDriversList"));
const SysOpsDriverDetailPage = lazy(() => import("@/pages/sysops/SysOpsDriverDetail"));
const SysOpsDriverRosterPage = lazy(() => import("@/pages/sysops/SysOpsDriverRoster"));
const SysOpsCustomersListPage = lazy(() => import("@/pages/sysops/SysOpsCustomersList"));
const SysOpsCustomerDetailPage = lazy(() => import("@/pages/sysops/SysOpsCustomerDetail"));
const SysOpsUsersListPage = lazy(() => import("@/pages/sysops/SysOpsUsersList"));
const SysOpsUserDetailPage = lazy(() => import("@/pages/sysops/SysOpsUserDetail"));
const SysOpsCreateUserPage = lazy(() => import("@/pages/sysops/SysOpsCreateUser"));
const SysOpsStoreDetailPage = lazy(() => import("@/pages/sysops/SysOpsStoreDetail"));
const SysOpsStoresListPage = lazy(() => import("@/pages/sysops/SysOpsStoresList"));
const SysOpsLocationsListPage = lazy(() => import("@/pages/sysops/SysOpsLocationsList"));
const SysOpsLocationDetailPage = lazy(() => import("@/pages/sysops/SysOpsLocationDetail"));
const SysOpsTransactionsListPage = lazy(() => import("@/pages/sysops/SysOpsTransactionsList"));
const SysOpsTransactionDetailPage = lazy(() => import("@/pages/sysops/SysOpsTransactionDetail"));
const SysOpsSettingsPage = lazy(() => import("@/pages/sysops/SysOpsSettings"));
const SysOpsOrdersListPage = lazy(() => import("@/pages/sysops/SysOpsOrdersList"));
const SysOpsWaybillReprintPage = lazy(() => import("@/pages/sysops/SysOpsWaybillReprint"));
const SysOpsManifestsListPage = lazy(() => import("@/pages/sysops/SysOpsManifestsList"));
const SysOpsManifestDetailPage = lazy(() => import("@/pages/sysops/SysOpsManifestDetail"));
const SysOpsOpsToolsPage = lazy(() => import("@/pages/sysops/SysOpsOpsTools"));
const SysOpsVinLabelGeneratorPage = lazy(() => import("@/pages/sysops/SysOpsVinLabelGenerator"));
const TransactionDetailPage = lazy(() => import("@/pages/transactions/TransactionDetail"));
const Error404Page = lazy(() => import("@/pages/Error404"));

function ReceivingHubDetailsRedirect() {
  const { hubId } = useParams<{ hubId: string }>();
  return <Navigate to={hubId ? `/hubs/${hubId}` : "/hubs"} replace />;
}

function ReceivingAtHubRedirect() {
  const { hubId } = useParams<{ hubId: string }>();
  return <Navigate to={hubId ? `/hubs/${hubId}/receiving` : "/hubs"} replace />;
}

function StoreOrdersTokenRedirect() {
  const { token } = useParams<{ token: string }>();
  const encodedToken = token ? encodeURIComponent(token) : "";
  return (
    <Navigate to={encodedToken ? `/orders?storeAccessToken=${encodedToken}` : "/orders"} replace />
  );
}

function LegacyAnalyticsRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return (
    <Navigate
      to={
        slug
          ? `/dashboard/analytics/${slug}`
          : dashboardAnalyticsHref(DEFAULT_DASHBOARD_ANALYTICS_SLUG)
      }
      replace
    />
  );
}

// Create router with React Router v6
export const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <GlobalRouteError />,
    children: [
      {
        path: "/login",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: "/register",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <RegisterPage />
          </Suspense>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "/signup",
        element: <Navigate to="/register" replace />,
      },
      {
        path: "/change-password",
        element: <Navigate to="/forgot-password" replace />,
      },
      {
        path: "/track-order",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <TrackOrderPage />
          </Suspense>
        ),
      },
      {
        path: "/track-order/:orderId",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <TrackOrderDetailPage />
          </Suspense>
        ),
      },
      {
        path: "/track-order-return/:waybill",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <TrackOrderReturnPage />
          </Suspense>
        ),
      },
      {
        path: "/shopify",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <ShopifyCallbackPage />
          </Suspense>
        ),
      },
      {
        path: "/shopify-success",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <ShopifySuccessPage />
          </Suspense>
        ),
      },
      {
        path: "/woocommerce",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <WooCommerceCallbackPage />
          </Suspense>
        ),
      },
      {
        path: "/",
        element: <ProtectedRoute />,
        children: [
          {
            index: true,
            element: <HomeEntryRedirect />,
          },
          {
            path: "dashboard",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: "dashboard/analytics",
            element: (
              <Navigate to={dashboardAnalyticsHref(DEFAULT_DASHBOARD_ANALYTICS_SLUG)} replace />
            ),
          },
          {
            path: "dashboard/analytics/:slug",
            element: <AdminSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <JourneyAnalyticsPage />
                  </Suspense>
                ),
              },
            ],
          },
          { path: "create-store", element: <Navigate to="/stores/create" replace /> },
          { path: "all-stores", element: <Navigate to="/stores" replace /> },
          { path: "store-settings", element: <Navigate to="/stores" replace /> },
          { path: "edit-store", element: <Navigate to="/stores" replace /> },
          { path: "managers", element: <Navigate to="/users" replace /> },
          {
            path: "all-managers",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <UsersPage />
              </Suspense>
            ),
          },
          {
            path: "add-manager",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <CreateUserPage />
              </Suspense>
            ),
          },
          { path: "account", element: <Navigate to="/settings" replace /> },
          {
            path: "payment-success",
            element: <Navigate to={{ pathname: "/settings", search: "tab=billing" }} replace />,
          },
          {
            path: "generate-access-token",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <GenerateStoreAccessTokenPage />
              </Suspense>
            ),
          },
          {
            path: "create-custom-store",
            element: <AdminSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <AdminCreateCustomStorePage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "get-order-key",
            element: <AdminSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <AdminGetOrderKeyPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "store-settings/mintsoft/:storeName/add",
            element: <AdminSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <AdminMintsoftCredentialsPage />
                  </Suspense>
                ),
              },
            ],
          },
          { path: "manifest-orders", element: <Navigate to="/manifests" replace /> },
          {
            path: "manifest-history",
            element: <Navigate to={{ pathname: "/manifests", search: "tab=collected" }} replace />,
          },
          { path: "store-orders/:token", element: <StoreOrdersTokenRedirect /> },
          {
            path: "store-manager",
            children: [
              { index: true, element: <Navigate to="/store-manager/orders" replace /> },
              {
                path: "orders",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <OrdersPage schemaMode="v4" />
                      </Suspense>
                    ),
                  },
                  {
                    path: "legacy",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <OrdersPage schemaMode="v3" />
                      </Suspense>
                    ),
                  },
                  {
                    path: "create",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <CreateOrderV4Page />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "order-details/:id",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <OrderDetailRouter />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "order/update/:id",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <EditOrderPage />
              </Suspense>
            ),
          },
          {
            path: "order-details/:id",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <OrderDetailRouter />
              </Suspense>
            ),
          },
          {
            path: "users/update/:id",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <EditUserPage />
              </Suspense>
            ),
          },
          {
            path: "orders",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <OrdersPage schemaMode="v4" />
                  </Suspense>
                ),
              },
              {
                path: "legacy",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <OrdersPage schemaMode="v3" />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CreateOrderV4Page />
                  </Suspense>
                ),
              },
              {
                path: ":id",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <OrderDetailRouter />
                      </Suspense>
                    ),
                  },
                  {
                    path: "edit",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <EditOrderPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "shipments/:waybill",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <ShipmentV4DetailPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "stores",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <StoresPage />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CreateStorePage />
                  </Suspense>
                ),
              },
              {
                path: ":id",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <StoreDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "edit",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <EditStorePage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "users",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <UsersPage />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CreateUserPage />
                  </Suspense>
                ),
              },
              {
                path: ":id",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <UserDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "edit",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <EditUserPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "manifests",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <ManifestsPage />
                  </Suspense>
                ),
              },
              {
                path: "collected",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CollectedManifestsPage />
                  </Suspense>
                ),
              },
              {
                path: ":manifestId",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <ManifestDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "orders/:id",
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingSpinner fullScreen />}>
                            <OrderDetailRouter />
                          </Suspense>
                        ),
                      },
                      {
                        path: "edit",
                        element: (
                          <Suspense fallback={<LoadingSpinner fullScreen />}>
                            <EditOrderPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "collected-manifests",
            element: <Navigate to={{ pathname: "/manifests", search: "tab=collected" }} replace />,
          },
          {
            path: "address-validation",
            element: <AddressValidationSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <AddressValidationQueuePage />
                  </Suspense>
                ),
              },
              {
                path: ":shipmentId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <AddressValidationComparePage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "problem-solve",
            element: <ProblemSolveSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <ProblemSolveQueuePage />
                  </Suspense>
                ),
              },
              {
                path: ":waybill",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <ProblemSolveShipmentPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "hubs",
            element: <AdminSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <ReceivingHubsPage />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <ReceivingCreateHubPage />
                  </Suspense>
                ),
              },
              {
                path: ":hubId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <ReceivingUpdateHubPage />
                  </Suspense>
                ),
              },
              {
                path: ":hubId/receiving",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <ReceivingAtHubPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "receiving",
            element: <AdminSectionOutlet />,
            children: [
              {
                index: true,
                element: <Navigate to="/hubs" replace />,
              },
              {
                path: "create",
                element: <Navigate to="/hubs/create" replace />,
              },
              {
                path: "update/:hubId",
                element: <ReceivingHubDetailsRedirect />,
              },
              {
                path: ":hubId",
                element: <ReceivingAtHubRedirect />,
              },
            ],
          },
          {
            path: "analytics",
            element: (
              <Navigate to={dashboardAnalyticsHref(DEFAULT_DASHBOARD_ANALYTICS_SLUG)} replace />
            ),
          },
          {
            path: "analytics/:slug",
            element: <LegacyAnalyticsRedirect />,
          },
          {
            path: "sysops",
            element: <AdminSectionOutlet />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsHomePage />
                  </Suspense>
                ),
              },
              {
                path: "hubs",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsHubsListPage />
                  </Suspense>
                ),
              },
              {
                path: "hubs/create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCreateHubPage />
                  </Suspense>
                ),
              },
              {
                path: "hubs/:hubId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsHubDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "merchants",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsMerchantsListPage />
                  </Suspense>
                ),
              },
              {
                path: "merchants/:merchantId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsMerchantDetailPage />
                  </Suspense>
                ),
              },
              ...(MERCHANT_BILLING_ENABLED
                ? [
                    {
                      path: "merchants/:merchantId/billing",
                      element: (
                        <Suspense fallback={<LoadingSpinner fullScreen />}>
                          <SysOpsMerchantBillingPage />
                        </Suspense>
                      ),
                    },
                  ]
                : []),
              {
                path: "merchants/create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCreateMerchantPage />
                  </Suspense>
                ),
              },
              {
                path: "merchant-onboarding",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsMerchantOnboardingPage />
                  </Suspense>
                ),
              },
              {
                path: "merchant-onboarding/:merchantId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsMerchantOnboardingDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "driver-onboarding",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsDriverOnboardingPage />
                  </Suspense>
                ),
              },
              {
                path: "driver-appeals",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsDriverAppealsPage />
                  </Suspense>
                ),
              },
              {
                path: "driver-onboarding/:applicationId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsDriverOnboardingDetailPage />
                  </Suspense>
                ),
              },
              ...(MERCHANT_BILLING_ENABLED
                ? [
                    {
                      path: "billing-exceptions",
                      element: (
                        <Suspense fallback={<LoadingSpinner fullScreen />}>
                          <SysOpsBillingExceptionsPage />
                        </Suspense>
                      ),
                    },
                  ]
                : []),
              {
                path: "promise-exceptions",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsPromiseExceptionsPage />
                  </Suspense>
                ),
              },
              {
                path: "customers",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCustomersListPage />
                  </Suspense>
                ),
              },
              {
                path: "customer/:customerId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCustomerDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "rates",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsRatesListPage />
                  </Suspense>
                ),
              },
              {
                path: "rates/:rateId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsRateDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "rates/create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCreateRatePage />
                  </Suspense>
                ),
              },
              {
                path: "service-types",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsServiceTypesListPage />
                  </Suspense>
                ),
              },
              {
                path: "service-types/:serviceTypeId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsServiceTypeDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "service-types/create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCreateServiceTypePage />
                  </Suspense>
                ),
              },
              {
                path: "geofences",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsGeofencesListPage />
                  </Suspense>
                ),
              },
              {
                path: "geofences/:geofenceId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsGeofenceDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "geofences/create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCreateGeofencePage />
                  </Suspense>
                ),
              },
              {
                path: "geofences/update/:geofenceId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsUpdateGeofencePage />
                  </Suspense>
                ),
              },
              {
                path: "geofences-v2",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsDriverZoneGeofencesPage />
                  </Suspense>
                ),
              },
              {
                path: "geofences-v2/plans",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsZonePlansListPage />
                  </Suspense>
                ),
              },
              {
                path: "geofences-v2/plans/:planId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsZonePlanDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "events",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsEventsListPage />
                  </Suspense>
                ),
              },
              {
                path: "events/:eventId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsEventDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "users",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsUsersListPage />
                  </Suspense>
                ),
              },
              {
                path: "users/:userId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsUserDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "users/create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCreateUserPage />
                  </Suspense>
                ),
              },
              {
                path: "drivers",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsDriversListPage />
                  </Suspense>
                ),
              },
              {
                path: "drivers/roster",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsDriverRosterPage />
                  </Suspense>
                ),
              },
              {
                path: "drivers/:driverId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsDriverDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "stores",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsStoresListPage />
                  </Suspense>
                ),
              },
              {
                path: "stores/:storeId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsStoreDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "stores/:storeId/locations/:locationId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsLocationDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "locations",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsLocationsListPage />
                  </Suspense>
                ),
              },
              {
                path: "locations/:locationId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsLocationDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "transactions",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsTransactionsListPage />
                  </Suspense>
                ),
              },
              {
                path: "transactions/:transactionId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsTransactionDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "manifests",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsManifestsListPage />
                  </Suspense>
                ),
              },
              {
                path: "address-validation",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <AddressValidationQueuePage />
                  </Suspense>
                ),
              },
              {
                path: "address-validation/:shipmentId",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <AddressValidationComparePage />
                  </Suspense>
                ),
              },
              {
                path: "manifests/:manifestId",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <SysOpsManifestDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "orders/:id",
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingSpinner fullScreen />}>
                            <OrderDetailRouter />
                          </Suspense>
                        ),
                      },
                      {
                        path: "edit",
                        element: (
                          <Suspense fallback={<LoadingSpinner fullScreen />}>
                            <EditOrderPage />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: "settings",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: "orders",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsOrdersListPage />
                  </Suspense>
                ),
              },
              {
                path: "orders/waybill-reprint",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsWaybillReprintPage />
                  </Suspense>
                ),
              },
              {
                path: "orders/:id",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <OrderDetailRouter />
                  </Suspense>
                ),
              },
              {
                path: "ops-tools",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsOpsToolsPage />
                  </Suspense>
                ),
              },
              {
                path: "ops-tools/vin-qr-labels",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsVinLabelGeneratorPage />
                  </Suspense>
                ),
              },
              {
                path: "*",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <SysOpsCatchAllPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "transaction/:transactionId",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <TransactionDetailPage />
              </Suspense>
            ),
          },
          {
            path: "locations",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <LocationsPage />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CreateLocationPage />
                  </Suspense>
                ),
              },
              {
                path: ":id",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <LocationDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "edit",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <EditLocationPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "package-types",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <PackageTypesListPage />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CreatePackageTypePage />
                  </Suspense>
                ),
              },
              {
                path: ":id/edit",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <EditPackageTypePage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "customers",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CustomersPage />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<LoadingSpinner fullScreen />}>
                    <CreateCustomerPage />
                  </Suspense>
                ),
              },
              {
                path: ":id",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <CustomerDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "edit",
                    element: (
                      <Suspense fallback={<LoadingSpinner fullScreen />}>
                        <EditCustomerPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "woocommerce-success",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <WooCommerceSuccessPage />
              </Suspense>
            ),
          },
          {
            path: "settings",
            element: (
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <SettingsPage />
              </Suspense>
            ),
          },
          {
            path: "reports",
            element: <Navigate to="/dashboard" replace />,
          },
        ],
      },
      {
        path: "*",
        element: (
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Error404Page />
          </Suspense>
        ),
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
